import { useCallback, useEffect } from 'react';

import { useLazyGetScratchEventQuery } from '@/api/queries/scratch';
import { type ScratchTicketDto } from '@/common/types/scratch';
import {
  applyBoughtTicket,
  applyRevealedTicket,
} from '@/redux/reducers/scratchReducer';
import { useAppDispatch } from '@/redux/store';

import { WebsocketEmitters } from '../app/socket';
import { socket } from '../socket/socket';

/**
 * Subscribes to scratch-related WS events:
 *   • SCRATCH_TICKET_BOUGHT — paid flows (Stars/TON/USDT) deliver the
 *     freshly-created ticket here. Drop into state + refetch event.
 *   • SCRATCH_EVENT_END — auto-reveal during cron / lazy close. One
 *     event per auto-claimed ticket; we update state + refetch.
 *
 * After every event we also trigger a `getScratchEvent` refetch, since
 * the server is the source of truth for offer counters and active state.
 */
const useScratchEventListener = (): void => {
  const dispatch = useAppDispatch();
  const [refetchEvent] = useLazyGetScratchEventQuery();

  const handleBought = useCallback(
    (ticket: ScratchTicketDto) => {
      dispatch(applyBoughtTicket(ticket));
      void refetchEvent();
    },
    [dispatch, refetchEvent],
  );

  const handleEventEnd = useCallback(
    (payload: { ticket: ScratchTicketDto }) => {
      if (payload?.ticket) dispatch(applyRevealedTicket(payload.ticket));
      void refetchEvent();
    },
    [dispatch, refetchEvent],
  );

  useEffect(() => {
    socket.on(WebsocketEmitters.SCRATCH_TICKET_BOUGHT, handleBought);
    socket.on(WebsocketEmitters.SCRATCH_EVENT_END, handleEventEnd);

    return (): void => {
      socket.off(WebsocketEmitters.SCRATCH_TICKET_BOUGHT, handleBought);
      socket.off(WebsocketEmitters.SCRATCH_EVENT_END, handleEventEnd);
    };
  }, [handleBought, handleEventEnd]);
};

export { useScratchEventListener };
